import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Grid, TextField } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { PriceListProfileService } from 'subModule/src/core/services';
import { useSelector } from 'react-redux';
import { brandingInfo } from 'features/server/brandingSlice';
import { themes } from 'theme/themes';
import FormDialog from 'common/Modal/Modal';
import DropdownMenu from 'common/DropDownMenu/DropDownMenu';
import { PriceListUploadPreviewTable } from './PriceListUploadPreviewTable';
import {
  matrixToXlsxFile,
  parseExcelFileToMatrix,
  type PriceListSheetMatrix,
} from './priceListExcel';

export type PriceListType = 'package' | 'service' | 'item';

type DownloadApiType = 'pkg' | 'svc' | 'item';

const DOWNLOAD_TYPE_MAP: Record<PriceListType, DownloadApiType> = {
  package: 'pkg',
  service: 'svc',
  item: 'item',
};

const FILE_NAME_MAP: Record<PriceListType, string> = {
  package: 'PackagePriceList.xlsx',
  service: 'ServicePriceList.xlsx',
  item: 'ItemPriceList.xlsx',
};

const SAMPLE_FILE_NAME_MAP: Record<PriceListType, string> = {
  package: 'PackagesSample.xlsx',
  service: 'ServicesSample.xlsx',
  item: 'PartsSample.xlsx',
};

const TYPE_LABELS: Record<PriceListType, string> = {
  package: 'packages',
  service: 'services',
  item: 'items',
};

/** Matches useInfiniteQuery keys in usePriceList.ts so the table refetches fresh rows after upload. */
const PRICELIST_TABLE_QUERY_KEY: Record<PriceListType, readonly string[]> = {
  item: ['get-pricelists-items-data'],
  package: ['get-pricelists-packages-data'],
  service: ['get-pricelists-services-data'],
};

interface PriceListActionsProps {
  type: PriceListType;
  pricelistId: number;
  /** Optional: e.g. reset form + refetch; list refresh is handled via React Query. */
  onUploadSuccess?: () => void | Promise<void>;
}

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const PriceListActions: React.FC<PriceListActionsProps> = ({
  type,
  pricelistId,
  onUploadSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [uploadModal, setUploadModal] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [parsedMatrix, setParsedMatrix] = useState<PriceListSheetMatrix>([]);
  const matrixForSubmitRef = useRef<PriceListSheetMatrix>([]);
  const [activeSheetName, setActiveSheetName] = useState('Sheet1');
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const closeModal = () => {
    setUploadModal(false);
    setFileName('');
    setShowPreview(false);
    setParsedMatrix([]);
    matrixForSubmitRef.current = [];
    setActiveSheetName('Sheet1');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownload = () => {
    setDownloadLoading(true);
    PriceListProfileService.downloadPriceList(DOWNLOAD_TYPE_MAP[type], {
      page: 0,
      size: 10000,
      pricelistId,
    })
      .then((res: Blob) => {
        triggerBlobDownload(
          new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          FILE_NAME_MAP[type]
        );
        enqueueSnackbar(
          <AlertMessages text={t('priceList') + ' ' + t('downloadedSuccessfully')} />,
          { variant: 'success' }
        );
      })
      .catch((err: any) => {
        if (!err) return;
        const message = err?.data?.message || err?.message || 'Something went wrong';
        enqueueSnackbar(<AlertMessages text={message} />, { variant: 'error' });
      })
      .finally(() => setDownloadLoading(false));
  };

  const handleSampleDownload = () => {
    setSampleLoading(true);
    PriceListProfileService.downloadPriceList(DOWNLOAD_TYPE_MAP[type], {
      page: 0,
      size: 10000,
      pricelistId,
    })
      .then((res: Blob) => {
        triggerBlobDownload(
          new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          SAMPLE_FILE_NAME_MAP[type]
        );
        enqueueSnackbar(
          <AlertMessages text={t('sampleDownloadedSuccessfully')} />,
          { variant: 'success' }
        );
      })
      .catch((err: any) => {
        if (!err) return;
        const message = err?.data?.message || err?.message || 'Something went wrong';
        enqueueSnackbar(<AlertMessages text={message} />, { variant: 'error' });
      })
      .finally(() => setSampleLoading(false));
  };

  const handlePreviewMatrixChange = (next: PriceListSheetMatrix) => {
    matrixForSubmitRef.current = next;
  };

  const handleUploadFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const file = formData.get('file') as File;
    if (!file || !file.name) return;

    setParseLoading(true);
    try {
      const { matrix, sheetName } = await parseExcelFileToMatrix(file);
      if (!matrix.length) {
        enqueueSnackbar(<AlertMessages text={t('noRecordFound')} />, { variant: 'warning' });
        return;
      }
      setParsedMatrix(matrix);
      matrixForSubmitRef.current = matrix;
      setActiveSheetName(sheetName);
      setShowPreview(true);
    } catch {
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
    } finally {
      setParseLoading(false);
    }
  };

  const submitEditedWorkbook = () => {
    const matrix = matrixForSubmitRef.current;
    if (!matrix.length) {
      enqueueSnackbar(<AlertMessages text={t('noRecordFound')} />, { variant: 'warning' });
      return;
    }
    setSubmitLoading(true);
    const fileToUpload = matrixToXlsxFile(
      matrix,
      activeSheetName,
      fileName || FILE_NAME_MAP[type]
    );
    PriceListProfileService.uploadPriceList(type, pricelistId, fileToUpload)
      .then(async (response: any) => {
        // Many backends return HTTP 200 without a `success` flag; only treat explicit false as failure.
        if (response?.success === false) {
          enqueueSnackbar(<AlertMessages text={response?.message || 'Upload failed'} />, {
            variant: 'error',
          });
          return;
        }
        enqueueSnackbar(
          <AlertMessages
            statusCode={response?.status}
            text={t('priceList') + ' ' + t('updated') + ' ' + t('successfully')}
          />,
          { variant: 'success' }
        );
        // Listing hooks use enabled:false; default refetchQueries only hits "active" queries and can skip them.
        // Invalidate + refetch with type 'all' forces the pricelist tables to reload without a full page refresh.
        await queryClient.invalidateQueries({
          queryKey: PRICELIST_TABLE_QUERY_KEY[type],
          exact: false,
        });
        await queryClient.refetchQueries({
          queryKey: PRICELIST_TABLE_QUERY_KEY[type],
          exact: false,
          type: 'all',
        });
        await Promise.resolve(onUploadSuccess?.());
        closeModal();
      })
      .catch((err: any) => {
        if (!err) return;
        const message = err?.data?.message || err?.message || 'Something went wrong';
        enqueueSnackbar(<AlertMessages text={message} />, { variant: 'error' });
      })
      .finally(() => setSubmitLoading(false));
  };

  return (
    <Box display="flex" gap={0} alignItems="center" flexWrap="wrap" className="justify-end gap-[10px]">
      {/* ── Download current price list ── */}
      <Button
        id="pricelist-export-button"
        variant="contained"
        disableElevation
        onClick={handleDownload}
        disabled={downloadLoading}
        className="bg-white font-Saira font-[600] text-[13px] h-[31px] px-4 py-1 cursor-pointer rounded-[6px] ms-[10px]"
        sx={{ outline: '2px solid', color: themeColors?.secondary, borderColor: themeColors?.secondary }}
      >
        {downloadLoading ? (
          <CircularProgress sx={{ color: themeColors?.secondary, marginRight: '8px' }} size={16} />
        ) : null}
        {t('download')}
      </Button>

      {/* ── Upload dropdown (matches Vehicle Listing pattern) ── */}
      <Box>
        <Button
          id="pricelist-import-button"
          variant="contained"
          disableElevation
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
          endIcon={<KeyboardArrowDownIcon />}
          className="bg-white font-Saira font-[600] text-[13px] h-[31px] px-4 py-1 cursor-pointer rounded-[6px] ms-[10px]"
          sx={{ outline: '2px solid', color: themeColors?.primary, borderColor: themeColors?.primary }}
        >
          {sampleLoading ? (
            <CircularProgress sx={{ color: themeColors?.primary }} size={24} />
          ) : (
            t('Upload')
          )}
        </Button>
        <DropdownMenu
          anchorEl={anchorEl}
          menuItems={[
            {
              label: `${t('Download Sample')} ${t('(Excel)')}`,
              onClick: handleSampleDownload,
            },
            {
              label: `${t('Upload')} ${t('(Excel)')}`,
              onClick: () => setUploadModal(true),
            },
          ]}
          onClose={() => setAnchorEl(null)}
          hoverColor={themeColors?.primary}
        />
      </Box>

      {/* ── Upload modal ── */}
      {uploadModal && (
        <FormDialog
          open={uploadModal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== 'backdropClick' && !parseLoading && !submitLoading) closeModal();
          }}
          title={(`${showPreview ? t('Imported') : t('Upload')} ${t(TYPE_LABELS[type])}`).replace(/\s+/g, ' ').trim()}
          createPopup={true}
          hideActions={true}
          fullWidth={showPreview}
          content={
            <Grid
              container
              className="items-center"
              component="form"
              noValidate={false}
              spacing={1}
              onSubmit={handleUploadFormSubmit}
            >
              {!showPreview ? (
                <>
                  <Grid item xs={12} md={8} id="importfile">
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="file"
                      required
                      accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                      style={{ color: fileName ? 'inherit' : '#fff' }}
                      onChange={(e) => setFileName(e.target.files?.[0]?.name || '')}
                      title={fileName}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      type="submit"
                      disabled={parseLoading}
                      margin="dense"
                      value={t('Upload')}
                      InputProps={{
                        startAdornment: parseLoading ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: themeColors?.primary, position: 'absolute', left: '10px' }}
                          />
                        ) : null,
                      }}
                      sx={{
                        '& div': { borderRadius: '8px', border: '0 !important' },
                        '& input': {
                          fontFamily: "'Saira', sans-serif",
                          fontSize: '14px',
                          lineHeight: '28px',
                          fontWeight: 600,
                          color: '#fff',
                          padding: '0.52rem 2.22rem 0.6rem 2.22rem',
                          boxShadow:
                            '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
                          backgroundImage: `linear-gradient(135deg, ${themeColors?.secondary}, ${themeColors?.secondary + 'B3'})`,
                          border: '0 !important',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          height: '25px',
                        },
                        '& .MuiInputBase-root': { padding: 0 },
                      }}
                    />
                  </Grid>
                </>
              ) : null}

              {showPreview ? (
                <>
                  <Grid item xs={12}>
                    <PriceListUploadPreviewTable
                      matrix={parsedMatrix}
                      onMatrixChange={handlePreviewMatrixChange}
                    />
                  </Grid>
                  <Grid item xs={12} id="form" className="flex justify-end gap-2">
                    <Button
                      className="secondary"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={closeModal}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      disabled={submitLoading}
                      className="primary"
                      style={{ background: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={submitEditedWorkbook}
                    >
                      {submitLoading && (
                        <CircularProgress
                          size={15}
                          sx={{ color: '#fff', position: 'absolute', left: '5px' }}
                        />
                      )}
                      {t('submit')}
                    </Button>
                  </Grid>
                </>
              ) : null}
            </Grid>
          }
        />
      )}
    </Box>
  );
};

export default PriceListActions;
